/**
 * Semáforo de las reglas de Meta que devuelve el validador del backend, una por fila.
 *
 * El orden es el de la urgencia: primero lo que impide mandar la plantilla (errores), después lo
 * que conviene revisar (advertencias) y al final lo que ya está bien, colapsado. Mostrar las
 * reglas en verde importa: el operador ve QUÉ se revisó, no sólo que «no hay errores», y un
 * semáforo que sólo aparece cuando algo falla no enseña cuáles son las reglas.
 *
 * Mientras `validando`, el encabezado dice «Revisando…» aunque haya un resultado anterior: ese
 * resultado es de otro texto y no puede decir «Todo en verde».
 */

import { useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2, XCircle } from "lucide-react";
import type { ReglaResultado } from "../types";
import { num, plural } from "../../campana-nueva/utils/format";

interface Props {
  reglas: ReglaResultado[];
  validando: boolean;
  /** true: no lista las reglas en verde (se usa bajo cada campo del editor) */
  soloFallidas?: boolean;
}

export function SemaforoReglas({ reglas, validando, soloFallidas = false }: Props) {
  const [verOk, setVerOk] = useState(false);
  const lista = reglas || [];
  const errores = lista.filter((r) => r.nivel === "error" && !r.ok);
  const advertencias = lista.filter((r) => r.nivel === "advertencia" && !r.ok);
  const enVerde = lista.filter((r) => r.ok);
  // Sin reglas todavía (primera validación en curso o fallida) no hay nada que dé verde.
  const sinResultado = lista.length === 0;

  let encabezado: { texto: string; cls: string; icono: JSX.Element };
  if (validando) {
    encabezado = { texto: "Revisando…", cls: "text-slate-500", icono: <Loader2 size={16} className="animate-spin text-slate-400" /> };
  } else if (sinResultado) {
    encabezado = { texto: "Todavía no pudimos revisar la plantilla.", cls: "text-slate-500", icono: <AlertTriangle size={16} className="text-amber-500" /> };
  } else if (errores.length) {
    encabezado = {
      texto: `Hay ${num(errores.length)} ${plural(errores.length, "cosa", "cosas")} para corregir`,
      cls: "text-red-700",
      icono: <XCircle size={16} className="text-red-600" />,
    };
  } else {
    encabezado = { texto: "Todo en verde: se puede mandar a Meta", cls: "text-emerald-700", icono: <CheckCircle2 size={16} className="text-emerald-600" /> };
  }

  return (
    <div className="rounded-xl border border-slate-100 bg-white">
      <div className={`flex items-center gap-2 px-4 py-3 border-b border-slate-100 text-sm font-semibold ${encabezado.cls}`} aria-live="polite">
        {encabezado.icono}
        <span>{encabezado.texto}</span>
        {!validando && !errores.length && advertencias.length > 0 && (
          <span className="ml-auto text-[11px] font-medium text-amber-700">
            {num(advertencias.length)} {plural(advertencias.length, "advertencia", "advertencias")}
          </span>
        )}
      </div>

      <ul className="divide-y divide-slate-50">
        {errores.map((r) => (
          <FilaRegla key={`e-${r.id}`} regla={r} icono={<XCircle size={15} className="text-red-600 flex-shrink-0 mt-0.5" />} cls="text-red-700" />
        ))}
        {advertencias.map((r) => (
          <FilaRegla key={`a-${r.id}`} regla={r} icono={<AlertTriangle size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />} cls="text-amber-800" />
        ))}
      </ul>

      {!soloFallidas && enVerde.length > 0 && (
        <div className="border-t border-slate-100">
          <button
            type="button"
            onClick={() => setVerOk((v) => !v)}
            aria-expanded={verOk}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50/50"
          >
            {verOk ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <CheckCircle2 size={14} className="text-emerald-600" />
            {num(enVerde.length)} {plural(enVerde.length, "regla en verde", "reglas en verde")}
          </button>
          {verOk && (
            <ul className="divide-y divide-slate-50 pb-1">
              {enVerde.map((r) => (
                <FilaRegla key={`ok-${r.id}`} regla={r} icono={<CheckCircle2 size={15} className="text-emerald-600 flex-shrink-0 mt-0.5" />} cls="text-slate-600" />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function FilaRegla({ regla, icono, cls }: { regla: ReglaResultado; icono: JSX.Element; cls: string }) {
  return (
    <li className="flex items-start gap-2 px-4 py-2.5">
      {icono}
      {/* Una regla sin mensaje (el backend no le puso texto) muestra su id: nunca una fila vacía. */}
      <span className={`text-sm leading-snug ${cls}`}>{regla.mensaje || regla.id}</span>
    </li>
  );
}

/** Las reglas fallidas de un campo, debajo del campo. Sin reglas, no ocupa lugar. */
export function ErroresDeCampo({ reglas }: { reglas?: ReglaResultado[] }) {
  if (!reglas?.length) return null;
  return (
    <ul className="mt-1.5 space-y-1">
      {reglas.map((r) => (
        <li key={r.id} className={`flex items-start gap-1.5 text-xs ${r.nivel === "error" ? "text-red-600" : "text-amber-700"}`}>
          {r.nivel === "error" ? <XCircle size={13} className="flex-shrink-0 mt-px" /> : <AlertTriangle size={13} className="flex-shrink-0 mt-px" />}
          <span>{r.mensaje || r.id}</span>
        </li>
      ))}
    </ul>
  );
}

export default SemaforoReglas;
